import React, {useEffect, useMemo} from "react"
import {useTranslation} from "react-i18next";
import {Button, Typography} from "@mui/material";
import Grid from "@mui/material/Grid2"
import { RegisterInfo, Restriction } from "../../Redux/reservationRedux";
import { useSelector } from 'react-redux'
import { RootState } from '../../Redux'

interface VehicleNoticeProps {
    registerInfo: RegisterInfo
    handleContinue: () => void
    checkTrafficMass: boolean
    checkAdditionalGroups: boolean
}

// Notice that these codes are used in TPSUOTIEDOTSUPPEAHAKUOUT message
// and different codes are used in KATSAKATSASTUSTMPHAKUOUT message used in \Prowess\Inspect\Entity\Vehicle::isOnlineCheckInAllowed
// following codes are allowed to continue with all subCodes
// 1 = Muutoskielto
// 3 = Ajokielto
// 4 = Autoverovapaus
// 5 = Myyntirajoitus
// 10 = Määräaikaiskatsastus suorittamatta/hyväksymättä
// 13 = Trafin muutoskielto
// 14 = Muutoskielto
// 16 = Kiinnitystieto
// 26 = Autoveron palautus
// 32 = Lisäkilpi
// 33 = Ajoneuvolla luovutusilmoitus
// 35 = Katsastuksen ajoaika <pvm> loppuun
// following codes are allowed to continue with given subCodes
// 18 = Liikenteestä poisto (01)
const codesThatAllowContinue = ['01','03','04','05','10','13','14','16','26','32','33','35']
const codesThatAllowContinueWithSubCode = [{type: '18', subType: '01'}]

/**
 * Checks if the given Restriction allows continuing
 * @param restriction The restriction to check
 * @returns True if the restriction allows continuing, false otherwise
 */
const isAllowedToContinue = (restriction: Restriction): boolean => {
    if (codesThatAllowContinue.includes(restriction.type)) {
        return true
    }

    return restriction.subType && codesThatAllowContinueWithSubCode.some(item =>
        item.type === restriction.type && item.subType === restriction.subType);
}

const restrictedAdditionalGroups = ['29']

const VehicleNotices = ({registerInfo, handleContinue, checkTrafficMass, checkAdditionalGroups}: VehicleNoticeProps) => {
    const { t } = useTranslation()

    const vehicleMaxTotalMassM1 = useSelector((state: RootState) => state.ui.settings?.booking?.options?.vehicleMaxTotalMassM1)
    const vehicleMaxTotalMassM1Message = useSelector((state: RootState) => state.ui.settings?.booking?.options?.vehicleMaxTotalMassM1Message)

    const hasRestrictions = useMemo(() => {
        if (! registerInfo?.restrictions) {
            return false
        }
        return ! registerInfo.restrictions.every((restriction: Restriction) => isAllowedToContinue(restriction))
    }, [registerInfo.restrictions])

    const hasMassRestriction = useMemo(() => {
        if (checkTrafficMass && vehicleMaxTotalMassM1 && registerInfo?.trafficMaxTotalMass && registerInfo?.trafficMaxTotalMass > vehicleMaxTotalMassM1) {
            if (['M1', 'M1G'].includes(registerInfo.vehicleClass?.id)) {
                return true
            }
        }

        return false
    }, [checkTrafficMass, vehicleMaxTotalMassM1, registerInfo]);

    const hasAdditionalGroupRestriction = useMemo(() => {
        return checkAdditionalGroups && (registerInfo?.additionalGroups?.some(group => restrictedAdditionalGroups.includes(group)) ?? false);
    }, [checkAdditionalGroups, registerInfo])

    const hasVehicleGroupRestriction = useMemo(() => {
        return 'MUU' === registerInfo.vehicleClass?.id
    }, [registerInfo]);

    const isAfterInspectionPeriodClosed = useMemo(() => {
        return registerInfo ? (registerInfo?.isAfterInspectionPeriodClosed ?? false) : null
    }, [registerInfo])

    const isPreviousInspectionInterrupted = useMemo(() => {
        return registerInfo ? (registerInfo?.isPreviousInspectionInterrupted ?? false) : null
    }, [registerInfo])

    useEffect(() => {
        if (hasRestrictions === false
            && hasMassRestriction === false
            && hasAdditionalGroupRestriction === false
            && hasVehicleGroupRestriction === false
            && isAfterInspectionPeriodClosed === false
            && isPreviousInspectionInterrupted === false
        ) {
            handleContinue()
        }
    }, [hasRestrictions, hasMassRestriction, hasAdditionalGroupRestriction, hasVehicleGroupRestriction, handleContinue, isAfterInspectionPeriodClosed, isPreviousInspectionInterrupted]);

    if (hasRestrictions === false && hasMassRestriction === false && hasAdditionalGroupRestriction === false && hasVehicleGroupRestriction === false && isAfterInspectionPeriodClosed === false && isPreviousInspectionInterrupted === false) {
        return null
    }

    return (
        <Grid container wrap="nowrap" direction="column" justifyContent="space-between" alignItems="center" spacing={4} className="pageMainContainer">
            <Grid>
            </Grid>
            <Grid>
                <Typography variant="h4" gutterBottom>{t('selfService.checkoutNotification')}</Typography>
            </Grid>
            <Grid className="contentWithMargin">
                {hasRestrictions ?
                    <Typography gutterBottom>{t('selfService.noticeNotAllowed')}</Typography>
                    :
                    (hasMassRestriction || hasAdditionalGroupRestriction ?
                        <Typography gutterBottom>{vehicleMaxTotalMassM1Message || t('selfService.vehicleNokMassM1')}</Typography>
                        :
                        (hasVehicleGroupRestriction || isAfterInspectionPeriodClosed || isPreviousInspectionInterrupted ?
                            <Typography gutterBottom>{t('selfService.vehicleNoticeGoToDesk')}</Typography>
                            :
                            null
                        )
                    )
                }
            </Grid>
            <Grid>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => window.location.reload()}
                >
                    {t('selfService.returnToStartScreen')}
                </Button>
            </Grid>
            <Grid style={{width: '100%'}}>
            </Grid>
        </Grid>
    )
}

export default VehicleNotices
